import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import Popup from 'reactjs-popup';

import UserMenu from '../../UserMenu/components/UserMenu';
import { Conductor } from '@opendate-app/setlist';
import NavMenuIcon from '../../shared/Icons/NavMenuIcon';
import UnreadDot from '../../shared/components/UnreadDot';
import OpendateLogo from '../../shared/Icons/OpendateLogo';
import UniversalSearch from '../../shared/components/UniversalSearch/UniversalSearch';

const popupContentStyle = {
  background: 'var(--sl-bg-primary-alt)',
  border: '1px solid var(--sl-border-secondary)',
  borderRadius: '12px',
  boxShadow: 'var(--sl-shadow-lg)',
  width: 'calc(100% - 30px)',
  padding: '0px',
  overflow: 'hidden',
  zIndex: '1060',
};

const Row = ({ item }) => (
  <a href={item.href} className={'main-menu-link d-flex align-items-center' + (item.active ? ' active' : '')}>
    <NavMenuIcon xlinkHref={item.xlinkHref} />
    <span className="menu-label">{item.toolTip}</span>
    {item.badge != null && <span className="nav-v2-badge">{item.badge}</span>}
  </a>
);

const MobileMenuV2 = ({ navigationV2Menu, settingsLink, helpLink, myTicketsLink, team, isGuest, csrfToken, currentUser, teamMemberships, searchUrl, searchSections }) => {
  const { anchors, groups, footer } = navigationV2Menu;
  const [open, setOpen] = useState(false);
  const [openGroups, setOpenGroups] = useState(() =>
    groups.filter((g) => g.items && g.items.some((i) => i.active)).map((g) => g.key)
  );

  useEffect(() => { document.body.classList.add('nav-v2'); }, []);

  const toggleGroup = (key) =>
    setOpenGroups((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));

  return (
    <div>
      <div className="m-topbar d-flex align-items-center justify-content-between">
        <OpendateLogo height={28} />
        <button type="button" className="m-burger" aria-label="Open menu" onClick={() => setOpen(true)}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
            <path d="M3 6h18M3 12h18M3 18h18" />
          </svg>
        </button>
      </div>

      <div className={'nav-v2-drawer-scrim' + (open ? ' open' : '')} onClick={() => setOpen(false)} />

      <aside className={'nav-v2-drawer' + (open ? ' open' : '')}>
        <div className="nav-v2-drawer-head d-flex align-items-center justify-content-between">
          <OpendateLogo height={30} />
          <button type="button" className="m-burger" aria-label="Close menu" onClick={() => setOpen(false)}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="nav-v2-drawer-nav">
          {searchUrl && <UniversalSearch searchUrl={searchUrl} sections={searchSections} layout="drawer" />}
          {anchors.map((a) => (
            <div key={a.key} className="nav-v2-anchor">
              <Row item={a} />
              {(a.subItems || []).map((s) => <div key={s.key} className="nav-v2-subitem"><Row item={s} /></div>)}
            </div>
          ))}
          {groups.map((g) => {
            // Standalone link (e.g. Reports) — a plain nav item, not a collapsible group.
            if (g.standalone) {
              return <div key={g.key} className="nav-v2-anchor"><Row item={g} /></div>;
            }
            const isOpen = openGroups.includes(g.key);
            const isActive = g.items && g.items.some((i) => i.active);
            return (
              <div key={g.key} className={'nav-v2-group' + (isOpen ? ' open' : '') + (isActive ? ' active' : '')}>
                <button type="button" className={'nav-v2-group-header main-menu-link' + (isActive ? ' active' : '')} onClick={() => toggleGroup(g.key)} aria-label={g.label}>
                  <span className="position-relative d-inline-flex">
                    <NavMenuIcon xlinkHref={g.icon} />
                    {g.unread && <UnreadDot label={g.unreadLabel} />}
                  </span>
                  <span className="menu-label">{g.label}</span>
                  <span className={'nav-v2-chev' + (isOpen ? ' up' : '')} aria-hidden="true">▾</span>
                </button>
                {isOpen && (
                  <div className="nav-v2-submenu">
                    {g.items.map((i) => <div key={i.key} className="nav-v2-subitem"><Row item={i} /></div>)}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="nav-v2-drawer-foot">
          {team.package?.ai_features && !isGuest && (
            <button type="button" className="main-menu-link d-flex align-items-center"
                  style={{ cursor: 'pointer' }}
                  data-conductor-launcher="true"
                  onClick={() => {
                    setOpen(false);
                    window.dispatchEvent(new CustomEvent('openConductor', { detail: { source: 'mobile_menu' } }));
                  }}>
              <Conductor className="conductor-nav-icon" width={20} height={20} />
              <span className="menu-label">Conductor</span>
            </button>
          )}
          {navigationV2Menu.onboarding && <Row item={navigationV2Menu.onboarding} />}
          {footer.map((f) => <Row key={f.key} item={f} />)}
          {Object.keys(helpLink).length > 0 && <Row item={helpLink} />}
          {Object.keys(settingsLink).length > 0 && <Row item={settingsLink} />}
          <div className="nav-v2-account">
            <Popup arrow={false}
                  offsetY={6}
                  offsetX={0}
                  position="top left"
                  contentStyle={popupContentStyle}
                  onOpen={(e) => { e.preventDefault(); }}
                  trigger={() => (
                    <div className="nav-v2-account-trigger d-flex align-items-center">
                      <div className="nav-v2-account-avatar-wrap">
                        <img src={currentUser.avatar_thumb_url} className="nav-v2-account-avatar" />
                        <div className="nav-v2-account-status-dot" />
                      </div>
                      <div className="nav-v2-account-info">
                        <div className="nav-v2-account-name">
                          {[currentUser.first_name, currentUser.last_name].filter(Boolean).join(' ')}
                        </div>
                        <div className="nav-v2-account-email">{currentUser.email}</div>
                      </div>
                    </div>
                  )}>
              {() => (
                <UserMenu csrfToken={csrfToken}
                          currentUser={currentUser}
                          team={team}
                          myTicketsLink={myTicketsLink}
                          teamMemberships={teamMemberships} />
              )}
            </Popup>
          </div>
        </div>
      </aside>
    </div>
  );
};

MobileMenuV2.propTypes = {
  navigationV2Menu: PropTypes.shape({
    anchors: PropTypes.array.isRequired,
    groups: PropTypes.array.isRequired,
    footer: PropTypes.array.isRequired,
    onboarding: PropTypes.object,
  }).isRequired,
  settingsLink: PropTypes.object,
  helpLink: PropTypes.object,
  myTicketsLink: PropTypes.object,
  team: PropTypes.object.isRequired,
  isGuest: PropTypes.bool,
  csrfToken: PropTypes.string.isRequired,
  currentUser: PropTypes.object.isRequired,
  teamMemberships: PropTypes.array.isRequired,
  searchUrl: PropTypes.string,
  searchSections: PropTypes.arrayOf(PropTypes.shape({ key: PropTypes.string, label: PropTypes.string })),
};
MobileMenuV2.defaultProps = { settingsLink: {}, helpLink: {}, myTicketsLink: {}, isGuest: false, searchUrl: null, searchSections: undefined };

export default MobileMenuV2;
